import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import './Layout.css';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen]   = useState(false);
  const [winWidth, setWinWidth]       = useState(window.innerWidth);
  const location = useLocation();
  const { showModal, setShowModal, SHORTCUTS } = useKeyboardShortcuts();

  /* Keep winWidth in sync */
  useEffect(() => {
    const onResize = () => setWinWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* Close mobile drawer on navigation */
  useEffect(() => { setMobileOpen(false); }, [location]);

  const isMobile = winWidth <= 768;
  const isTablet = winWidth > 768 && winWidth <= 1024;

  const toggleSidebar = () => {
    if (isMobile) setMobileOpen(prev => !prev);
    else          setSidebarOpen(prev => !prev);
  };

  /*
   * Sidebar "isOpen" = shows labels (full width).
   * Only true on desktop when the user hasn't collapsed it.
   */
  const sidebarIsOpen = !isMobile && !isTablet && sidebarOpen;

  /*
   * CSS class that shifts the content right.
   * 'sidebar-closed' → margin-left: 72px  (icon-only desktop)
   * (no class)       → margin-left: 260px (full desktop)
   * tablet / mobile  → overridden by @media rules in Layout.css
   */
  const contentClass = (!sidebarOpen && !isMobile && !isTablet) ? 'sidebar-closed' : '';

  return (
    <div className="layout">
      {/* Mobile overlay – tap to close drawer */}
      {isMobile && (
        <div
          className={`sidebar-overlay ${mobileOpen ? 'visible' : ''}`}
          onClick={() => setMobileOpen(false)}
        />
      )}

      <Sidebar
        isOpen={sidebarIsOpen}
        mobileOpen={mobileOpen}
        isMobile={isMobile}
      />

      {/* No inline marginLeft – Layout.css handles all breakpoints */}
      <div className={`main-content ${contentClass}`}>
        <Header toggleSidebar={toggleSidebar} />
        <div className="content-wrapper">
          {children}
        </div>
      </div>

      {/* Keyboard Shortcuts Modal */}
      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, backdropFilter:'blur(4px)' }}
          onClick={() => setShowModal(false)}>
          <div style={{ background:'#fff', borderRadius:20, padding:32, width:480, maxWidth:'90vw', boxShadow:'0 24px 80px #0004' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 style={{ margin:0, fontSize:18, fontWeight:800 }}>⌨️ Keyboard Shortcuts</h3>
              <button onClick={() => setShowModal(false)} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#94a3b8' }}>×</button>
            </div>
            {SHORTCUTS.map(s => (
              <div key={s.key} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 12px', borderRadius:8, marginBottom:4, background:'#f8fafc' }}>
                <span style={{ fontSize:14, color:'#0f172a' }}>{s.label}</span>
                <kbd style={{ background:'#e2e8f0', borderRadius:6, padding:'3px 10px', fontSize:12, fontWeight:700, color:'#475569', fontFamily:'monospace' }}>{s.key}</kbd>
              </div>
            ))}
            <p style={{ margin:'16px 0 0', fontSize:12, color:'#94a3b8', textAlign:'center' }}>Press <kbd style={{ background:'#e2e8f0', borderRadius:4, padding:'1px 6px', fontFamily:'monospace' }}>?</kbd> anywhere to toggle this panel · <kbd style={{ background:'#e2e8f0', borderRadius:4, padding:'1px 6px', fontFamily:'monospace' }}>Esc</kbd> to close</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
